import { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, Calendar, Clock, Pencil, Trash2, X, MessageSquare, CalendarClock } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

function fmtRange(start, end) {
  if (!start) return "Ohne Datum";
  const d = new Date(start);
  const datePart = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const startTime = start.includes("T") ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
  let endTime = "";
  if (end && end.includes("T")) {
    endTime = new Date(end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  }
  if (startTime && endTime) return `${datePart} · ${startTime} – ${endTime}`;
  if (startTime) return `${datePart} · ${startTime}`;
  if (endTime) return `${datePart} · bis ${endTime}`;
  return datePart;
}

export function CustomerConversationTab({ entries, onAdd, onUpdate, onDelete, saving, onRequestFollowup }) {
  const [text, setText] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [createFollowup, setCreateFollowup] = useState(false);

  const sorted = useMemo(() => {
    return [...(entries || [])].sort((a, b) => {
      const ka = a.datetime_start || a.created_at || "";
      const kb = b.datetime_start || b.created_at || "";
      return kb.localeCompare(ka);
    });
  }, [entries]);

  const resetForm = () => {
    setText("");
    setStart("");
    setEnd("");
    setEditingId(null);
    setCreateFollowup(false);
  };

  const handleNow = () => {
    const now = new Date();
    const date = start?.split("T")[0] || now.toISOString().split("T")[0];
    const time = now.toTimeString().slice(0, 5);
    // Nur die Bis-Zeit auf "jetzt" setzen; die Von-Zeit bleibt zur manuellen Eingabe leer.
    setEnd(`${date}T${time}`);
    // Datum sicherstellen (falls noch keines gewählt wurde), ohne eine Von-Zeit vorzugeben.
    if (!start) setStart(date);
  };

  const handleSubmit = async () => {
    const payload = { text, datetime_start: start || null, datetime_end: end || null };
    const wantFollowup = !editingId && createFollowup;
    const noteDate = start || end || null;
    const ok = editingId ? await onUpdate(editingId, payload) : await onAdd(payload);
    if (ok) {
      resetForm();
      if (wantFollowup && typeof onRequestFollowup === "function") onRequestFollowup(noteDate);
    }
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setText(entry.text || "");
    setStart(entry.datetime_start || "");
    setEnd(entry.datetime_end || "");
  };

  const plainEmpty = !text || !text.replace(/<[^>]*>/g, "").trim();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{editingId ? "Gesprächsnotiz bearbeiten" : "Neue Gesprächsnotiz"}</CardTitle>
            <CardDescription>
              {editingId
                ? "Ändern Sie den ausgewählten Eintrag und speichern Sie."
                : "Jede Notiz wird mit ihrem Datum/Zeitraum als eigener Eintrag gespeichert."}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {editingId && (
              <Button size="sm" variant="ghost" onClick={resetForm} data-testid="cancel-edit-conversation-btn">
                <X className="w-4 h-4 mr-2" />Abbrechen
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={saving || plainEmpty}
              data-testid="save-conversation-notes-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? "Aktualisieren" : "Eintrag speichern"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={start?.split("T")[0] || ""}
                onChange={(e) => {
                  const time = start?.split("T")[1] || "09:00";
                  setStart(e.target.value ? `${e.target.value}T${time}` : "");
                }}
                className="w-auto"
                data-testid="conversation-date-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <Input
                type="time"
                value={start?.split("T")[1] || ""}
                onChange={(e) => {
                  const date = start?.split("T")[0] || new Date().toISOString().split("T")[0];
                  setStart(e.target.value ? `${date}T${e.target.value}` : "");
                }}
                className="w-[120px]"
                data-testid="conversation-time-input"
              />
              <span className="text-muted-foreground">bis</span>
              <Input
                type="time"
                value={end?.split("T")[1] || ""}
                onChange={(e) => {
                  const date = start?.split("T")[0] || new Date().toISOString().split("T")[0];
                  setEnd(e.target.value ? `${date}T${e.target.value}` : "");
                }}
                className="w-[120px]"
                data-testid="conversation-time-end-input"
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleNow} data-testid="conversation-now-btn">
              Jetzt
            </Button>
          </div>
          <RichTextEditor value={text} onChange={setText} placeholder="Gesprächsnotizen eingeben..." />
          {!editingId && (
            <label className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground" data-testid="conversation-followup-label">
              <Checkbox
                checked={createFollowup}
                onCheckedChange={(c) => setCreateFollowup(!!c)}
                data-testid="conversation-followup-checkbox"
              />
              <CalendarClock className="w-4 h-4" />
              Nach dem Speichern eine Wiedervorlage (Folgeaufgabe) anlegen
            </label>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Verlauf ({sorted.length})</CardTitle>
          <CardDescription>Alle Gesprächsnotizen, neueste zuerst.</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground" data-testid="conversation-empty">
              <MessageSquare className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm">Noch keine Gesprächsnotizen erfasst.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="conversation-entries">
              {sorted.map((entry) => (
                <li
                  key={entry.id}
                  className="rounded-lg border bg-card p-3"
                  data-testid={`conversation-entry-${entry.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                      {fmtRange(entry.datetime_start, entry.datetime_end)}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEdit(entry)}
                        data-testid={`edit-conversation-${entry.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => onDelete(entry.id)}
                        data-testid={`delete-conversation-${entry.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div
                    className="prose prose-sm max-w-none mt-2 text-sm text-foreground/90"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.text || "") }}
                  />
                  {entry.created_by && (
                    <p className="mt-2 text-xs text-muted-foreground">Erfasst von {entry.created_by}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
